"use client";

import React, { useMemo, ViewTransition } from "react";
import { useInfiniteCardSets } from "modules/cards/hooks/useInfiniteCardSets";
import { useSetFilters } from "modules/sets/hooks/useSetFilters";
import type { ColumnDef } from "@tanstack/react-table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { DataTable } from "modules/common/components/Table/DataTable";
import { CardSetPaged } from "modules/cards/model/set";
import {
  firstUpper,
  getSetHeroTransitionName,
  toDateFormat,
} from "utils/utils";
import { formatCurrency } from "utils/currency";
import Link from "next/link";
import { DataTableError } from "modules/common/components/Table/DataTableError";
import { SetIcon } from "modules/common/components/SetIcon";
import { useSets } from "modules/common/hooks/useSets";
import { useParams } from "next/navigation";
import type { GameId } from "../../common/model/models";
import { getGameMetadata } from "modules/common/gameRegistry";
import { DataViewPanel } from "modules/common/components/DataViewPanel";
import {
  getSetTableNameColumnWidth,
  SET_TABLE_COLUMN_WIDTHS,
} from "modules/sets/utils/setTableLayout";

function SetTable() {
  const params = useParams<{ game: GameId }>();
  const presentation = getGameMetadata(params.game).presentation.setTable;

  // Get the sets request from nuqs URL state
  const { filters: setsReq } = useSetFilters();
  const { getSetName } = useSets();
  // Use the existing infinite query hook
  const {
    query: {
      data,
      isPending,
      isError,
      error,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    },
  } = useInfiniteCardSets(setsReq);

  // Flatten the data for the table
  const flattenedData = useMemo(
    () => data?.pages.flatMap((page) => page.sets) ?? [],
    [data]
  );

  // Define columns for the table using the react-table API
  const columns: ColumnDef<CardSetPaged>[] = [
    {
      id: "icon",
      header: () => null,
      meta: {
        className: presentation.showIcon ? "" : "hidden",
        width: `${SET_TABLE_COLUMN_WIDTHS.icon}%`,
      },
      cell: ({ row }) => {
        const set = row.original;
        return (
          <div className="px-0.5 text-center">
            <div
              className="tooltip tooltip-right tooltip-info"
              data-tip={set.name || getSetName(set.code, set.game)}
            >
              {/* Pairs with SetInfoDisplay's icon on the set page. */}
              <ViewTransition
                name={getSetHeroTransitionName(set)}
                share="vt-hero"
                default="none"
              >
                <SetIcon
                  setCode={set.code}
                  game={set.game}
                  set={set}
                  className="text-secondary"
                />
              </ViewTransition>
            </div>
          </div>
        );
      },
    },
    {
      id: "name",
      header: "Name",
      meta: {
        width: getSetTableNameColumnWidth(presentation),
      },
      cell: ({ row }) => {
        const set = row.original;
        return (
          <Link
            href={`/sets/${set.game}/${set.slug.toLowerCase()}`}
            className="text-primary block w-full max-w-[40vw] overflow-hidden text-ellipsis whitespace-nowrap text-sm no-underline hover:underline md:max-w-none"
          >
            {set.name}
          </Link>
        );
      },
    },
    {
      id: "code",
      header: "Code",
      cell: ({ row }) => (
        <span className="text-secondary pl-1 text-sm">{row.original.code}</span>
      ),
      meta: {
        className: "max-md:hidden",
        width: `${SET_TABLE_COLUMN_WIDTHS.code}%`,
      },
    },
    {
      id: "totalSetSize",
      header: "Cards",
      meta: {
        className: presentation.showTotalSize ? "max-md:hidden" : "hidden",
        width: `${SET_TABLE_COLUMN_WIDTHS.totalSetSize}%`,
      },
      cell: ({ row }) => (
        <span className="text-secondary pl-1 text-sm">
          {row.original.totalSetSize}
        </span>
      ),
    },
    {
      id: "marketCap",
      header: "Market Cap",
      cell: ({ row }) => {
        const marketCap = row.original.marketCap;
        return (
          <span className="text-secondary pl-1 text-sm">
            {marketCap && marketCap > 0
              ? formatCurrency(marketCap, row.original.pricing.currency)
              : null}
          </span>
        );
      },
      meta: {
        className: "hidden md:table-cell",
        width: `${SET_TABLE_COLUMN_WIDTHS.marketCap}%`,
      },
    },
    {
      id: "type",
      header: "Type",
      cell: ({ row }) => {
        const type = row.original.type;
        return (
          type && (
            <span className="text-secondary pl-1 text-sm">
              {firstUpper(type)}
            </span>
          )
        );
      },

      meta: {
        className: presentation.showType ? "hidden md:table-cell" : "hidden",
        width: `${SET_TABLE_COLUMN_WIDTHS.type}%`,
      },
    },
    {
      id: "releaseDate",
      header: "Released",
      meta: {
        width: `${SET_TABLE_COLUMN_WIDTHS.releaseDate}%`,
      },
      cell: ({ row }) => {
        const releaseDate = row.original.releaseDate;
        if (!releaseDate || releaseDate.startsWith("0001-01-01")) {
          return null;
        }

        return (
          <span className="text-secondary block min-w-fit text-sm">
            {toDateFormat(releaseDate)}
          </span>
        );
      },
    },
  ];

  // Create the react-table instance
  const table = useReactTable({
    data: flattenedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (set) => `${set.game}:${set.code}`,
  });

  // Handle error state
  if (isError) {
    return (
      <DataTableError
        error={error}
        fallbackMessage="An error occurred while loading sets"
      />
    );
  }

  return (
    // Measured cap instead of `100svh-220px`: that constant undershot the
    // header, so the table ran past the fold and the page scrolled behind it.
    <DataViewPanel>
      <DataTable
        table={table}
        isLoading={isPending}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        rowCount={20}
        emptyMessage="No sets found"
        emptyDescription="Try adjusting your filters or search criteria"
        containerClassName="relative h-full overflow-y-auto max-lg:overflow-x-hidden table-auto"
      />
    </DataViewPanel>
  );
}

export default SetTable;
